import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import ChartTitle from '../Tabs/ChartTitle';

/**
 * Componente de gráfico de barras para exibir mensagens por dia.
 * 
 * @param {Object} props
 * @param {string} props.titulo - Título exibido acima do gráfico
 * @param {Array<string>} props.labels - Rótulos do eixo X (dias)
 * @param {Array<number>} props.data - Valores correspondentes aos rótulos (quantidade de mensagens)
 * @param {string} [props.cor='#4caf50'] - Cor da barra (opcional)
 */
const GraficoMensagensPorDia = ({
  titulo = 'Mensagens por Dia',
  labels = [],
  data = [],
  cor = '#667eea',
  legendaY = 'Mensagens'
}) => {
  const theme = useTheme();
  // Calcula o total de mensagens
  const totalMensagens = data.reduce((acc, curr) => acc + curr, 0);

  const truncatedLabels = labels.map(label =>
    label.length > 5 ? label.substring(0, 5) + '...' : label
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 2,
        borderRadius: 2,
        backgroundColor: 'background.paper',
        p: 1.5,
        pl: 2.5,
        mt: 1.5
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 200,
          mx: 'auto',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          width: '100%',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            transform: { xs: 'none', sm: 'rotate(-90deg)' }, // Rotaciona o texto em 90° em telas maiores
            whiteSpace: 'nowrap',             // Evita quebra de linha no texto
            mb: { xs: 1, sm: 0 },             // Margem inferior no mobile
            mr: { xs: 0, sm: -8.5 },            // Ajuste de margem direita no desktop
            ml: { xs: 0, sm: -6 },            // Ajuste de margem esquerda no desktop
            fontSize: { xs: '14px', sm: '16px' }, // Tamanho de fonte responsivo
            textAlign: 'center',              // Centraliza o texto
            color: 'text.secondary',
          }}
        >
          {legendaY}
        </Typography>

        {/* Linha divisória entre o label Y e o gráfico */}
        <Box
          sx={{
            width: { xs: '100%', sm: '1px' },  // Linha horizontal no mobile, vertical no desktop
            height: { xs: '1px', sm: '100%' }, // Altura ajustada conforme o layout
            backgroundColor: 'divider',
            mb: { xs: 1, sm: 0 },              // Margem inferior no mobile
          }}
        />

        {/* Container do gráfico */}
        <Box
          aria-label={`Gráfico de barras: ${titulo}`}
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
          }}
        >
          {/* Componente de título do gráfico */}
          <ChartTitle>{titulo}</ChartTitle>

          {/* Componente de gráfico de barras do MUI X Charts */}
          <BarChart
            series={[{
              data,
              color: cor,
              showLabel: true,
            }]}

            xAxis={[{
              id: 'x-axis',
              data: truncatedLabels,
              scaleType: 'band',
              paddingInner: 0.5,
              paddingOuter: 0.3,
              tickLabelStyle: {
                fontSize: 12,
                fill: theme.palette.text.secondary,
                angle: -90,
                textAnchor: 'end',
              },
              tickSize: 0,
              label: '',
            }]}

            yAxis={[{
              id: 'y-axis',
              tickLabelStyle: {
                fontSize: 12,
                fill: theme.palette.text.secondary,
              },
              tickSize: 0,
              tickPadding: 2, // quanto menor, mais próximo
              label: '',
            }]}

            grid={{ horizontal: false, vertical: false }}
            margin={{ top: 20, right: 30, bottom: 60, left: 70 }}
            height={250}
            borderRadius={10}

            components={{
              Bar: (props) => <rect {...props} rx={15} ry={15} />,
            }}

            sx={{
              '& .MuiBarElement-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>
      </Box>

      {/* Rodapé com total de atendimentos */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: 'center',
          justifyContent: { xs: 'space-between', sm: 'center' },
          gap: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: 'text.secondary',
            fontSize: '1rem',
            whiteSpace: 'nowrap'
          }}
        >
          Total Mensagens
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#667eea',
            fontSize: '1.1rem',
          }}
        >
          {totalMensagens}
        </Typography>
      </Box>
    </Box>
  );
};

export default GraficoMensagensPorDia;
